/* global ol */
export default class OlMapManager {
  constructor() {
    this.map = null
    this.mapCallbacks = {
      click: {},
    }
    this.view = null
  }

  getMap() {
    return this.map
  }

  addMapInteraction(interaction) {
    this.map.addInteraction(interaction)
  }

  addView(params) {
    this.view = new ol.View({
      projection: 'EPSG:3857', // default
      center: params.center || [0, 0],
      zoom: params.zoom || 4,
      minZoom: params.minZoom || 4,
      maxZoom: params.maxZoom || 18,
    })
    return this.view
  }

  getView() {
    return this.view
  }

  getLayers() {
    return this.map.layers
  }

  getLayer(name) {
    return this.map.layers[name]
  }

  addLayer(layer, opts = {}) {
    if (!this.map) {
      console.log('Map undefined.')
      return null
    }
    if (!layer.name) {
      console.log('Need layer name.')
      return null
    }

    this.removeLayer(layer.name)

    this.map.layers[layer.name] = layer
    if (isNaN(opts.insertAt)) {
      this.map.addLayer(layer)
    } else {
      this.map.getLayers().insertAt(opts.insertAt, layer)
    }
    if (opts.interaction) {
      this.addLayerInteraction(layer.name, opts.interaction)
    }

    this.updateSize()
    return layer
  }

  removeLayer(name) {
    if (this.map.layers[name]) {
      this.map.removeLayer(this.map.layers[name])
      delete this.map.layers[name]
      this.removeLayerInteraction()
    }
  }

  isLayerVisible(name) {
    return this.map.layers[name].getVisible()
  }

  setLayerVisible(name, isVisible) {
    if (!this.map) {
      console.log('Map undefined')
      return
    }

    if (!this.map.layers[name]) {
      console.log('Layer undefined')
      return
    }

    this.map.layers[name].setVisible(isVisible)
  }

  addLayerInteraction(layerName, params) {
    Object.keys(params).forEach(eventName => {
      if (!this.mapCallbacks[eventName]) {
        throw new Error(`Unsupported event name "${eventName}"` +
          ' given at addLayerInteraction')
      }
      this.mapCallbacks[eventName][layerName] = params[eventName]
    })
  }

  removeLayerInteraction(layerName) {
    Object.keys(this.mapCallbacks).forEach(eventName => {
      delete this.mapCallbacks[eventName][layerName]
    })
  }

  getMarkerLayers() {
    return this.map.markers
  }

  getMarkerLayer(name) {
    return this.map.markers[name]
  }

  /* Add Marker Layer
  **
  ** Param
  **  - element (String)
  **  - name (String)
  */
  addMarkerLayer(name) {
    if (!this.map) {
      console.log('Map undefined')
      return null
    }

    if (!this.map.markers[name]) {
      var layer = new ol.layer.Vector({
        source: new ol.source.Vector({ features: [] })
      })

      this.map.markers[name] = layer
      this.map.addLayer(layer)
    }

    this.updateSize()
    return this.map.markers[name]
  }

  setMarkerLayer(name) {
    if (!this.map) {
      console.log('Map undefined')
      return null
    }

    this.removeMarkerLayer(name)
    this.addMarkerLayer(name)
  }

  removeMarkerLayer(name) {
    if (this.map.markers[name]) {
      this.map.removeLayer(this.map.markers[name])
      delete this.map.markers[name]
    }
  }

  isMarkerLayerVisible(name) {
    if (!this.map) {
      console.log('Map undefined')
      return
    }
    if (!this.map.markers[name]) {
      console.log('Layer undefined')
      return
    }
    return this.map.markers[name].getVisible()
  }

  setMarkerLayerVisible(name, isVisible) {
    if (!this.map) {
      console.log('Map undefined')
      return
    }
    if (!this.map.markers[name]) {
      console.log('Layer undefined')
      return
    }

    this.map.markers[name].setVisible(isVisible)
  }

  getExtent() {
    return this.view.calculateExtent(this.map.getSize())
  }

  getZoom() {
    return this.view.getZoom()
  }

  setZoom(to) {
    if (!this.view) {
      console.log('View undefined')
      return
    }

    this.view.setZoom(to)
  }

  getCenter() {
    return this.view.getCenter()
  }

  setCenter(to) {
    if (!this.view) {
      console.log('View undefined')
      return
    }

    this.view.setCenter(to)
  }

  /*
  fromLonLat(coord) {
    return ol.proj.fromLonLat(coord)
  }

  transform(coord) {
    return ol.proj.transform(coord, 'EPSG:3857', 'EPSG:4326')
  }

  transformExtent(extent) {
    return ol.proj.transformExtent(extent, 'EPSG:3857', 'EPSG:4326')
  }
  */

  updateSize() {
    if (!this.map) {
      console.log('Map undefined')
      return
    }
    this.map.updateSize()
  }

  /* Add Marker
  **
  ** Param
  ** - setting
  **   type = Object
  **   data =
  **   - id (String)
  **   - element (String)
  **   - layerName (String)
  **   - coord (Array)
  **   - anchor (Array)
  **   - icon (String)
  */
  addMarker(setting) {
    if (!this.map) {
      console.log('Map undefined')
      return
    }

    if (!this.map.markers[setting.layerName]) {
      console.log('Marker layer undefined')
      return
    }

    var geom = new ol.geom.Point(ol.proj.fromLonLat(setting.coord))
    var feature = new ol.Feature(geom)
    feature.setStyle([
      new ol.style.Style({
        image: new ol.style.Icon(({
          anchor: setting.anchor || [0.5, 0.5],
          anchorXUnits: 'fraction',
          anchorYUnits: 'fraction',
          opacity: 1,
          src: setting.icon || '',
        }))
      })
    ])
    feature.setId(setting.id)
    this.map.markers[setting.layerName].getSource().addFeature(feature)
  }

  animate(setting) {
    this.view.animate(setting)
  }

  fitPoints(point) {
    var ext = ol.extent.boundingExtent(point)
    ext = ol.proj.transformExtent(
      ext,
      ol.proj.get('EPSG:4326'),
      ol.proj.get('EPSG:3857')
    )
    this.view.fit(ext, { duration: 2000 })
  }

  /* Initialize Openlayers Maps
  **
  ** Param
  ** - setting
  **   type = Object
  **   data =
  **   - element (String)
  **   - center (Array)
  **   - zoom (Number)
  **   - enableAttribution (Boolean)
  **   - enablePan (Boolean)
  **   - enableZoomButton (Boolean)
  **   - enableMouseWheelZoom (Boolean)
  **   - enableDoubleClickZoom (Boolean)
  **   - enableScaleLine (Boolean)
  */
  initMap(params) {
    if (this.map) {
      console.log('Map already exists')
      return
    }

    var controls = ol.control.defaults({
      attribution: !!params.enableAttribution,
      zoom: !!params.enableZoomButton,
    })

    if (params.enableScaleLine) {
      controls = controls.extend([
        new ol.control.ScaleLine()
      ])
    }

    this.addView(params)

    this.map = new ol.Map({
      layers: [],
      controls: controls,
      target: params.element,
      view: this.getView(),
      interactions: ol.interaction.defaults({
        dragPan: !!params.enablePan,
        mouseWheelZoom: !!params.enableMouseWheelZoom,
        doubleClickZoom: !!params.enableDoubleClickZoom,
      })
    })

    this.map.layers = {}
    this.map.markers = {}

    // disguise event handler, since
    // openlayer feature does not handle click events by itself.
    const evts = ['click']
    evts.forEach(evtName => {
      this.map.on(evtName, event => {
        this.map.forEachFeatureAtPixel(
          event.pixel,
          (feature, layer) => {
            const func = (this.mapCallbacks[evtName] || {})[layer.name]
            if (func) {
              func({ event, feature })
            }
          })
      })
    })

    return this.map
  }
}
